/**
 * LayerConfigBuilderWidget.js
 * ArcGIS Maps SDK for JavaScript 4.x
 *
 * A side-panel widget that:
 *  1. Accepts an ArcGIS Server REST services directory URL
 *  2. Traverses the directory tree (handles folders, sub-services)
 *  3. Lets the user pick layers via checkboxes
 *  4. Builds selected layerConfigs compatible with LayerManagerWidget
 *  5. Sends selected configs to parent callback for running-state updates
 *  6. Includes auth support: None | ArcGIS Token | Portal OAuth
 *
 * ── Usage ──────────────────────────────────────────────────────────────────
 *
 *   import LayerConfigBuilderWidget from './LayerConfigBuilderWidget.js';
 *   import './LayerConfigBuilderWidget.css';
 *
 *   const builder = new LayerConfigBuilderWidget({
 *     view    : mapView,       // optional — attaches panel to view container
 *     position: 'right',       // 'left' | 'right'
 *     title   : 'Layer Builder',
 *     onSave  : (configs) => { // called after every Save
 *       layerManagerWidget.addLayer(configs[0]);
 *     },
 *   });
 *
 * ── LayerConfig output shape ────────────────────────────────────────────────
 * Compatible with LayerManagerWidget. Extra fields (displayField, fields,
 * popupTemplate, sublayers) are passed through to the Esri layer constructor.
 *
 * @typedef {Object} LayerConfig
 * @property {string}  id
 * @property {string}  title
 * @property {'feature'|'map-image'|'imagery'|'wms'} type
 * @property {string}  url
 * @property {'point'|'polyline'|'polygon'|null} [geometryType]
 * @property {boolean} visible
 * @property {number}  opacity
 * @property {string}  [displayField]
 * @property {Object}  [symbol]
 * @property {Object}  [popupTemplate]
 * @property {Array}   [sublayers]       — MapImageLayer / WMSLayer
 * @property {Array}   [fields]          — raw field descriptors (internal)
 */

 

// ── Constants ──────────────────────────────────────────────────────────────────

const EXCLUDED_TYPES = new Set([
  'GPServer','GeometryServer','GeocodeServer','SearchServer',
  'MobileServer','GeodataServer','GlobeServer','NAServer',
  'SystemServer','SyncServer','StreamServer','RelationalCatalogServer',
  'WorkspaceServer','BigDataCatalogServer','NotebookServer',
]);
const SUPPORTED_TYPES = new Set(['FeatureServer','MapServer','ImageServer','WMSServer']);

const TYPE_BADGE = { FeatureServer:'F', MapServer:'M', ImageServer:'I', WMSServer:'W' };
const GEOM_ICON  = { point:'●', polyline:'╌', polygon:'◼', imagery:'▣', wms:'◉', null:'?' };

const STORAGE_KEY     = 'lcb_layerConfigs';
const STORAGE_URL_KEY = 'lcb_lastUrl';
const MAX_CONCURRENCY = 4;       // parallel requests cap
const REQUEST_TIMEOUT = 15000;   // ms

// ── Widget ─────────────────────────────────────────────────────────────────────

export default class LayerConfigBuilderWidget {

  /**
   * @param {Object}   options
   * @param {__esri.MapView|__esri.SceneView|null} [options.view]
   * @param {'left'|'right'} [options.position='right']
   * @param {string}   [options.title='Layer Config Builder']
   * @param {Function} [options.onSave]  - callback(configs[]) called after Save
   * @param {string}   [options.syncEndpoint] - REST endpoint for DB sync (future)
   */
  constructor ({
    view           = null,
    position       = 'right',
    title          = 'Layer Config Builder',
    onSave         = null,
    onCancel       = null,
    syncEndpoint   = null,
    esri           = null,
  } = {}) {
    this._view         = view;
    this._pos          = position;
    this._title        = title;
    this._onSaveCallback = onSave;
    this._onCancelCallback = onCancel;
    this._syncEndpoint = syncEndpoint;
    this._esri = esri || {};
    if (!this._esri.esriRequest || !this._esri.OAuthInfo || !this._esri.IdentityManager) {
      throw new Error('[LayerConfigBuilderWidget] Missing esri dependencies. Use createCombinedControlForEsriLoader(...) or pass options.esri.');
    }

    // ── Auth state ──────────────────────────────────────────────────────────
    this._authMode  = 'none';   // 'none' | 'token' | 'oauth'
    this._token     = null;
    this._portalUrl = '';

    // ── Tree state ──────────────────────────────────────────────────────────
    this._rootUrl     = localStorage.getItem(STORAGE_URL_KEY) || '';
    this._treeData    = null;   // { url, folders[], services[] }
    this._loading     = false;
    this._loadError   = null;

    // _layerDataMap: unique key → full layer config + metadata
    this._layerDataMap  = new Map();
    // _selectedKeys: Set of selected layer keys
    this._selectedKeys  = new Set();
    // Last config generated from current selection
    this._generatedCfg  = null;

    this._panel     = null;
    this._buildPanel();
    this._attachPanel();
  }

  // ── Panel ────────────────────────────────────────────────────────────────────

  _buildPanel () {
    this._panel = document.createElement('div');
    this._panel.className = `lcb-panel lcb-panel--${this._pos}`;
    this._panel.innerHTML = this._renderShell();
    this._bindPanelEvents();
  }

  _renderShell () {
    return `
      <div class="lcb-header">
        <span class="lcb-header-title">${this._title}</span>
        <button class="lcb-icon-btn" data-action="cancelDiscovery" title="Cancel">✕</button>
      </div>
      <div class="lcb-body">
        ${this._renderAuthSection()}
        ${this._renderUrlSection()}
        <div class="lcb-tree-wrap" id="lcb-tree"></div>
        <div class="lcb-footer" id="lcb-footer" hidden>
          <div class="lcb-footer-info">
            <span id="lcb-sel-count">0 layers selected</span>
          </div>
          <div class="lcb-footer-btns">
            <button class="lcb-btn-outline" data-action="clearAll">Clear All</button>
            <button class="lcb-btn-accent" data-action="saveSelection">Add Selected Layers</button>
          </div>
        </div>
      </div>`;
  }

  _renderAuthSection () {
    return `
      <div class="lcb-section lcb-section--collapsible">
        <button class="lcb-section-toggle" data-toggle-section="auth">
          <svg viewBox="0 0 14 14" width="12" height="12" fill="currentColor" style="flex-shrink:0">
            <path d="M7 1a3 3 0 0 1 3 3c0 1.5-.9 2.8-2.2 3.3L7.8 13H6.2l.8-5.7A3 3 0 0 1 7 1z
                     M5 5.5A2 2 0 1 0 9 5.5"/>
          </svg>
          <span>Authentication</span>
          <svg class="lcb-arrow" viewBox="0 0 10 6" width="10" fill="none"
               stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
            <polyline points="0,0 5,6 10,0"/>
          </svg>
        </button>
        <div class="lcb-section-body" data-section-id="auth" hidden>

          <div class="lcb-radio-group">
            <label class="lcb-radio"><input type="radio" name="lcb-auth" value="none" checked> None (public)</label>
            <label class="lcb-radio"><input type="radio" name="lcb-auth" value="token"> ArcGIS Token</label>
            <label class="lcb-radio"><input type="radio" name="lcb-auth" value="oauth"> OAuth / Portal</label>
          </div>

          <!-- Token panel -->
          <div class="lcb-auth-sub" data-auth-sub="token" hidden>
            <div class="lcb-tabs">
              <button class="lcb-tab active" data-tab-target="manualtoken">Manual token</button>
              <button class="lcb-tab"        data-tab-target="credentials">Username / Password</button>
            </div>
            <div class="lcb-tab-pane active" data-tab-pane="manualtoken">
              <input class="lcb-input" id="lcb-manual-token"
                type="text" placeholder="Paste token string…" autocomplete="off">
              <button class="lcb-btn-outline lcb-btn-sm" data-action="setManualToken">Set Token</button>
            </div>
            <div class="lcb-tab-pane" data-tab-pane="credentials">
              <input class="lcb-input" id="lcb-username" type="text" placeholder="Username" autocomplete="username">
              <input class="lcb-input" id="lcb-password" type="password" placeholder="Password" autocomplete="current-password">
              <input class="lcb-input" id="lcb-token-url" type="url"
                placeholder="Token endpoint (auto-detected if blank)">
              <p class="lcb-hint">Leave endpoint blank — widget derives it from your services URL.</p>
              <button class="lcb-btn-outline lcb-btn-sm" data-action="generateToken">Generate Token</button>
              <span class="lcb-status" id="lcb-token-status"></span>
            </div>
          </div>

          <!-- OAuth panel -->
          <div class="lcb-auth-sub" data-auth-sub="oauth" hidden>
            <input class="lcb-input" id="lcb-portal-url" type="url"
              placeholder="Portal URL — https://portal.domain.com/portal"
              value="${this._portalUrl}">
            <input class="lcb-input" id="lcb-app-id" type="text"
              placeholder="App ID (optional)">
            <button class="lcb-btn-outline lcb-btn-sm" data-action="oauthSignIn">Sign In via Portal</button>
            <span class="lcb-status" id="lcb-oauth-status"></span>
          </div>

        </div>
      </div>`;
  }

  _renderUrlSection () {
    return `
      <div class="lcb-section lcb-section--url">
        <div class="lcb-url-row">
          <input class="lcb-input lcb-url-input" id="lcb-root-url" type="url"
            placeholder="https://server/arcgis/rest/services | .../Folder | .../MapServer | .../MapServer/0"
            value="${this._rootUrl}">
          <button class="lcb-btn-accent" data-action="load" id="lcb-load-btn">Load</button>
        </div>
        <p class="lcb-hint">Paste a root, folder, service, or layer URL from ArcGIS REST.</p>
      </div>`;
  }

  // ── Event binding ─────────────────────────────────────────────────────────────

  _bindPanelEvents () {
    this._panel.addEventListener('click',  e => this._onClick(e));
    this._panel.addEventListener('change', e => this._onChange(e));
  }

  _onClick (e) {
    const btn = e.target.closest('[data-action],[data-toggle-section],[data-tab-target]');
    if (!btn) return;

    const action       = btn.dataset.action;
    const toggleSec    = btn.dataset.toggleSection;
    const tabTarget    = btn.dataset.tabTarget;

    if (toggleSec)  { this._toggleSection(toggleSec); return; }
    if (tabTarget)  { this._switchTab(btn, tabTarget); return; }

    switch (action) {
      case 'cancelDiscovery': this._onCancelDiscovery(); break;
      case 'load':          this._onLoad(); break;
      case 'setManualToken':this._onSetManualToken(); break;
      case 'generateToken': this._onGenerateToken(); break;
      case 'oauthSignIn':   this._onOAuthSignIn(); break;
      case 'saveSelection': this._onSave(); break;
      case 'clearAll':      this._onClearAll(); break;
      case 'syncServer':    this._onSync(); break;
      case 'expandSvc':     this._toggleServiceExpand(btn.dataset.svcKey); break;
      case 'selectAllSvc':  this._selectAllInService(btn.dataset.svcKey); break;
    }
  }

  _onChange (e) {
    const el = e.target;
    // Auth mode radio
    if (el.name === 'lcb-auth') {
      this._authMode = el.value;
      this._panel.querySelectorAll('[data-auth-sub]').forEach(p => {
        p.hidden = p.dataset.authSub !== el.value;
      });
      return;
    }
    // Layer checkbox
    if (el.dataset.layerKey) {
      if (el.checked) this._selectedKeys.add(el.dataset.layerKey);
      else            this._selectedKeys.delete(el.dataset.layerKey);
      this._updateSelectionUI();
    }
    // Service "select-all" checkbox
    if (el.dataset.svcAllKey) {
      const keys = [...this._layerDataMap.keys()].filter(k => k.startsWith(el.dataset.svcAllKey + '__'));
      keys.forEach(k => {
        if (el.checked) this._selectedKeys.add(k); else this._selectedKeys.delete(k);
        const cb = this._panel.querySelector(`input[data-layer-key="${k}"]`);
        if (cb) cb.checked = el.checked;
      });
      this._updateSelectionUI();
    }
  }

  _toggleSection (id) {
    const body = this._panel.querySelector(`[data-section-id="${id}"]`);
    const btn  = this._panel.querySelector(`[data-toggle-section="${id}"]`);
    if (!body) return;
    body.hidden = !body.hidden;
    btn?.classList.toggle('open', !body.hidden);
  }

  _switchTab (btn, target) {
    const bar  = btn.closest('.lcb-tabs');
    const panes = btn.closest('.lcb-auth-sub')?.querySelectorAll('.lcb-tab-pane');
    bar?.querySelectorAll('.lcb-tab').forEach(t => t.classList.remove('active'));
    btn.classList.add('active');
    panes?.forEach(p => p.classList.toggle('active', p.dataset.tabPane === target));
    panes?.forEach(p => p.hidden = p.dataset.tabPane !== target);
  }

  _onCancelDiscovery () {
    if (typeof this._onCancelCallback === 'function') this._onCancelCallback();
    this.destroy();
  }

  // ── Auth ──────────────────────────────────────────────────────────────────────

  _onSetManualToken () {
    const val = this._panel.querySelector('#lcb-manual-token')?.value.trim();
    if (!val) return;
    this._token = val;
    this._setStatus('lcb-token-status', '✓ Token set', 'ok');
  }

  async _onGenerateToken () {
    const { esriRequest, IdentityManager } = this._esri;
    const username = this._panel.querySelector('#lcb-username')?.value.trim();
    const password = this._panel.querySelector('#lcb-password')?.value;
    const customUrl = this._panel.querySelector('#lcb-token-url')?.value.trim();
    const rootUrl  = this._panel.querySelector('#lcb-root-url')?.value.trim();

    if (!username || !password) {
      this._setStatus('lcb-token-status', '⚠ Enter username and password', 'warn'); return;
    }

    // Derive token endpoint: /arcgis/tokens/generateToken from root services URL
    let tokenUrl = customUrl;
    if (!tokenUrl && rootUrl) {
      try {
        const u = new URL(rootUrl);
        // Find "/rest/services" and replace the path up to there
        const base = u.pathname.replace(/\/rest\/services.*$/, '');
        tokenUrl = `${u.origin}${base}/tokens/generateToken`;
      } catch { /* ignore */ }
    }
    if (!tokenUrl) { this._setStatus('lcb-token-status', '⚠ Could not derive token URL', 'warn'); return; }

    this._setStatus('lcb-token-status', '⏳ Generating…');
    try {
      const body = new URLSearchParams({
        username, password, client: 'requestip', expiration: '60', f: 'json',
      });
      const resp = await esriRequest(tokenUrl, {
        method      : 'post',
        body,
        responseType: 'json',
        timeout     : REQUEST_TIMEOUT,
      });
      if (resp.data?.token) {
        this._token = resp.data.token;
        this._setStatus('lcb-token-status', `✓ Token acquired (expires ${new Date(resp.data.expires).toLocaleTimeString()})`, 'ok');
        // Register with IdentityManager so esriRequest auto-appends it
        IdentityManager.registerToken({
          server : rootUrl,
          token  : this._token,
          expires: resp.data.expires,
        });
      } else {
        this._setStatus('lcb-token-status', `✗ ${resp.data?.error?.message || 'Unknown error'}`, 'err');
      }
    } catch (err) {
      this._setStatus('lcb-token-status', `✗ ${err.message}`, 'err');
    }
  }

  async _onOAuthSignIn () {
    const { OAuthInfo, IdentityManager } = this._esri;
    const portalUrl = this._panel.querySelector('#lcb-portal-url')?.value.trim();
    const appId     = this._panel.querySelector('#lcb-app-id')?.value.trim();
    if (!portalUrl) { this._setStatus('lcb-oauth-status', '⚠ Enter Portal URL', 'warn'); return; }

    this._portalUrl = portalUrl;
    this._setStatus('lcb-oauth-status', '⏳ Opening sign-in…');
    try {
      const info = new OAuthInfo({
        portalUrl,
        appId     : appId || undefined,
        flowType  : 'auto',
        popup     : true,
      });
      IdentityManager.registerOAuthInfos([info]);
      const cred = await IdentityManager.getCredential(`${portalUrl}/sharing`);
      this._token = cred.token;
      this._setStatus('lcb-oauth-status', `✓ Signed in as ${cred.userId}`, 'ok');
    } catch (err) {
      this._setStatus('lcb-oauth-status', `✗ ${err.message}`, 'err');
    }
  }

  // ── Directory traversal ───────────────────────────────────────────────────────

  async _onLoad () {
    const urlInput = this._panel.querySelector('#lcb-root-url');
    const rootUrl  = urlInput?.value.trim().replace(/\/+$/, '');
    if (!rootUrl) return;

    this._rootUrl = rootUrl;
    localStorage.setItem(STORAGE_URL_KEY, rootUrl);

    this._loading   = true;
    this._loadError = null;
    this._treeData  = null;
    this._layerDataMap.clear();
    this._selectedKeys.clear();

    this._renderTreeLoading();
    this._setFooterVisible(false);

    const loadBtn = this._panel.querySelector('#lcb-load-btn');
    if (loadBtn) { loadBtn.disabled = true; loadBtn.textContent = '…'; }

    try {
      this._treeData = await this._discoverFromUrl(rootUrl);
    } catch (err) {
      this._loadError = err;
    } finally {
      this._loading = false;
      if (loadBtn) { loadBtn.disabled = false; loadBtn.textContent = 'Load'; }
    }

    this._renderTree();
    this._setFooterVisible(this._layerDataMap.size > 0);
  }

  async _discoverFromUrl (inputUrl) {
    const mode = this._detectUrlMode(inputUrl);
    if (mode.kind === 'layer') {
      return this._discoverFromLayerUrl(mode.baseServiceUrl, mode.layerId, mode.serviceType);
    }
    if (mode.kind === 'service') {
      return this._discoverFromServiceUrl(mode.baseServiceUrl, mode.serviceType);
    }
    return this._traverseDirectory(mode.baseDirectoryUrl);
  }

  _detectUrlMode (url) {
    const clean = url.replace(/\/+$/, '');
    const layerMatch = clean.match(/^(.*)\/(FeatureServer|MapServer|ImageServer|WMSServer)\/(\d+)$/i);
    if (layerMatch) {
      return {
        kind: 'layer',
        baseServiceUrl: `${layerMatch[1]}/${layerMatch[2]}`,
        serviceType: layerMatch[2],
        layerId: Number(layerMatch[3]),
      };
    }
    const serviceMatch = clean.match(/^(.*)\/(FeatureServer|MapServer|ImageServer|WMSServer)$/i);
    if (serviceMatch) {
      return {
        kind: 'service',
        baseServiceUrl: clean,
        serviceType: serviceMatch[2],
      };
    }
    return {
      kind: 'directory',
      baseDirectoryUrl: clean,
    };
  }

  async _discoverFromServiceUrl (serviceUrl, serviceTypeRaw) {
    const serviceType = String(serviceTypeRaw || '').trim();
    const shortName = serviceUrl.split('/').slice(-2, -1)[0] || serviceUrl.split('/').pop();
    const svcKey = this._slug(`${shortName}_${serviceType}`);
    const svcDef = { name: shortName, type: serviceType };
    const service = await this._fetchServiceAndLayersFromUrl(serviceUrl, svcKey, shortName, svcDef);
    return {
      url: serviceUrl,
      folders: [],
      services: service ? [service] : [],
    };
  }

  async _discoverFromLayerUrl (serviceUrl, layerId, serviceTypeRaw) {
    const serviceType = String(serviceTypeRaw || '').trim();
    const shortName = serviceUrl.split('/').slice(-2, -1)[0] || serviceUrl.split('/').pop();
    const svcKey = this._slug(`${shortName}_${serviceType}`);
    const info = await this._fetch(serviceUrl);
    const service = {
      key: svcKey,
      name: shortName,
      fullName: shortName,
      type: serviceType,
      url: serviceUrl,
      layers: [],
    };

    if (serviceType === 'FeatureServer') {
      const basic = (info.layers || []).find(l => Number(l.id) === Number(layerId)) || { id: layerId, name: `Layer ${layerId}` };
      const full = await this._fetch(`${serviceUrl}/${layerId}`).catch(() => basic);
      const cfg = this._buildFeatureConfig(svcKey, serviceUrl, shortName, basic, full);
      if (cfg) { service.layers.push(cfg); this._layerDataMap.set(cfg._key, cfg); }
    } else if (serviceType === 'MapServer') {
      const basic = (info.layers || []).find(l => Number(l.id) === Number(layerId)) || { id: layerId, name: `Layer ${layerId}` };
      const full = await this._fetch(`${serviceUrl}/${layerId}`).catch(() => basic);
      const cfg = this._buildMapImageConfig(svcKey, serviceUrl, shortName, basic, full);
      if (cfg) { service.layers.push(cfg); this._layerDataMap.set(cfg._key, cfg); }
    } else if (serviceType === 'ImageServer') {
      const cfg = this._buildImageryConfig(svcKey, serviceUrl, shortName, info);
      if (cfg) { service.layers.push(cfg); this._layerDataMap.set(cfg._key, cfg); }
    } else if (serviceType === 'WMSServer') {
      const cfg = this._buildWMSConfig(svcKey, serviceUrl, shortName);
      if (cfg) { service.layers.push(cfg); this._layerDataMap.set(cfg._key, cfg); }
    }

    return {
      url: `${serviceUrl}/${layerId}`,
      folders: [],
      services: service.layers.length ? [service] : [],
    };
  }

  /** Fetch root directory → gather folders and root-level services. */
  async _traverseDirectory (directoryUrl) {
    const data = await this._fetch(directoryUrl);
    const tree = { url: directoryUrl, folders: [], services: [] };

    // Parallel fetch: root services + all folder directories
    const tasks = [];

    // Root-level services (name is plain, e.g. "SampleWorldCities")
    for (const s of (data.services || [])) {
      if (!SUPPORTED_TYPES.has(s.type)) continue;
      tasks.push({ kind: 'service', folder: null, svcDef: s });
    }

    // Fetch each folder directory first to get folder's services
    const folderNames = data.folders || [];
    const folderDataList = await this._fetchParallel(folderNames.map(f => `${directoryUrl}/${f}`));

    folderNames.forEach((folderName, i) => {
      const fd = folderDataList[i];
      if (!fd) return;
      const folder = { name: folderName, services: [] };
      tree.folders.push(folder);
      for (const s of (fd.services || [])) {
        if (!SUPPORTED_TYPES.has(s.type)) continue;
        tasks.push({ kind: 'service', folder, svcDef: s });
      }
    });

    // Now fetch all services in parallel (throttled)
    await this._runThrottled(tasks, async ({ folder, svcDef }) => {
      const svcInfo = await this._fetchServiceAndLayers(directoryUrl, svcDef);
      if (!svcInfo) return;
      if (folder) folder.services.push(svcInfo);
      else        tree.services.push(svcInfo);
    });

    return tree;
  }

  /**
   * Fetch service endpoint and its layers.
   * svcDef.name is full path: "FolderA/ServiceB" or "ServiceB"
   */
  async _fetchServiceAndLayers (rootUrl, svcDef) {
    // Construct service URL robustly for root or folder directory inputs.
    const svcUrl = this._resolveServiceUrl(rootUrl, svcDef.name, svcDef.type);
    let   info;
    try {
      info = await this._fetch(svcUrl);
    } catch {
      return null;
    }

    const svcShortName = svcDef.name.split('/').pop();
    const svcKey       = this._slug(`${svcDef.name}_${svcDef.type}`);
    const service = {
      key     : svcKey,
      name    : svcShortName,
      fullName: svcDef.name,
      type    : svcDef.type,
      url     : svcUrl,
      layers  : [],
    };

    if (svcDef.type === 'FeatureServer') {
      const rawLayers = info.layers || [];
      const layerInfos = await this._fetchParallel(
        rawLayers.map(l => `${svcUrl}/${l.id}`)
      );
      rawLayers.forEach((l, i) => {
        const full = layerInfos[i] || l;
        const cfg  = this._buildFeatureConfig(svcKey, svcUrl, svcShortName, l, full);
        if (cfg) { service.layers.push(cfg); this._layerDataMap.set(cfg._key, cfg); }
      });

    } else if (svcDef.type === 'MapServer') {
      const rawLayers = (info.layers || []).filter(l => !l.subLayerIds?.length);
      const layerInfos = await this._fetchParallel(
        rawLayers.map(l => `${svcUrl}/${l.id}`)
      );
      rawLayers.forEach((l, i) => {
        const full = layerInfos[i] || l;
        const cfg  = this._buildMapImageConfig(svcKey, svcUrl, svcShortName, l, full);
        if (cfg) { service.layers.push(cfg); this._layerDataMap.set(cfg._key, cfg); }
      });

    } else if (svcDef.type === 'ImageServer') {
      const cfg = this._buildImageryConfig(svcKey, svcUrl, svcShortName, info);
      service.layers.push(cfg);
      this._layerDataMap.set(cfg._key, cfg);

    } else if (svcDef.type === 'WMSServer') {
      const cfg = this._buildWMSConfig(svcKey, svcUrl, svcShortName);
      service.layers.push(cfg);
      this._layerDataMap.set(cfg._key, cfg);
    }

    return service.layers.length ? service : null;
  }

  _resolveServiceUrl (directoryUrl, serviceName, serviceType) {
    const dir = String(directoryUrl || '').replace(/\/+$/, '');
    const name = String(serviceName || '').replace(/^\/+|\/+$/g, '');
    const type = String(serviceType || '').replace(/^\/+|\/+$/g, '');
    // If serviceName is fully-qualified (contains folder path), anchor to /rest/services root.
    if (name.includes('/')) {
      const m = dir.match(/^(.*\/rest\/services)/i);
      const base = m ? m[1] : dir;
      return `${base}/${name}/${type}`;
    }
    // If directory is already a folder URL (.../rest/services/<Folder>)
    // then serviceName is usually short and should be appended directly.
    return `${dir}/${name}/${type}`;
  }

  async _fetchServiceAndLayersFromUrl (svcUrl, svcKey, svcShortName, svcDef) {
    let info;
    try {
      info = await this._fetch(svcUrl);
    } catch {
      return null;
    }
    const service = {
      key     : svcKey,
      name    : svcShortName,
      fullName: svcDef.name,
      type    : svcDef.type,
      url     : svcUrl,
      layers  : [],
    };

    if (svcDef.type === 'FeatureServer') {
      const rawLayers = info.layers || [];
      const layerInfos = await this._fetchParallel(rawLayers.map(l => `${svcUrl}/${l.id}`));
      rawLayers.forEach((l, i) => {
        const full = layerInfos[i] || l;
        const cfg  = this._buildFeatureConfig(svcKey, svcUrl, svcShortName, l, full);
        if (cfg) { service.layers.push(cfg); this._layerDataMap.set(cfg._key, cfg); }
      });
    } else if (svcDef.type === 'MapServer') {
      const rawLayers = (info.layers || []).filter(l => !l.subLayerIds?.length);
      const layerInfos = await this._fetchParallel(rawLayers.map(l => `${svcUrl}/${l.id}`));
      rawLayers.forEach((l, i) => {
        const full = layerInfos[i] || l;
        const cfg  = this._buildMapImageConfig(svcKey, svcUrl, svcShortName, l, full);
        if (cfg) { service.layers.push(cfg); this._layerDataMap.set(cfg._key, cfg); }
      });
    } else if (svcDef.type === 'ImageServer') {
      const cfg = this._buildImageryConfig(svcKey, svcUrl, svcShortName, info);
      service.layers.push(cfg);
      this._layerDataMap.set(cfg._key, cfg);
    } else if (svcDef.type === 'WMSServer') {
      const cfg = this._buildWMSConfig(svcKey, svcUrl, svcShortName);
      service.layers.push(cfg);
      this._layerDataMap.set(cfg._key, cfg);
    }

    return service.layers.length ? service : null;
  }

  // ── Config builders ───────────────────────────────────────────────────────────

  _buildFeatureConfig (svcKey, svcUrl, svcName, layerBasic, layerFull) {
    const id     = layerBasic.id;
    const name   = layerFull.name || layerBasic.name || `Layer ${id}`;
    const geom   = this._normalizeGeom(layerFull.geometryType);
    const key    = `${svcKey}__F${id}`;

    const rendererType = layerFull?.drawingInfo?.renderer?.type || '';
    return {
      _key        : key,
      _svcKey     : svcKey,
      _svcUrl     : svcUrl,
      _svcType    : 'FeatureServer',
      _svcName    : svcName,
      _layerId    : id,
      _geom       : geom,
      // ── LayerManagerWidget-compatible fields ──
      id          : this._slug(`${svcName}_${name}_${id}`),
      title       : name,
      type        : 'feature',
      url         : `${svcUrl}/${id}`,
      geometryType: geom,
      visible     : true,
      opacity     : 1,
      displayField: layerFull.displayField || '',
      minScale    : layerFull.minScale  ?? 0,
      maxScale    : layerFull.maxScale  ?? 0,
      symbol      : this._extractSymbol(layerFull.drawingInfo?.renderer, geom),
      rendererType: rendererType,
      isSymbolEditable: !rendererType || rendererType === 'simple',
      labelsVisible: !!(layerFull.drawingInfo?.labelingInfo?.length),
      labelingInfo : layerFull.drawingInfo?.labelingInfo || undefined,
      popupTemplate: this._buildPopup(name, layerFull.fields || [], layerFull.displayField),
      fields      : (layerFull.fields || []).map(f => ({ name: f.name, alias: f.alias, type: f.type })),
    };
  }

  _buildMapImageConfig (svcKey, svcUrl, svcName, layerBasic, layerFull) {
    const id   = layerBasic.id;
    const name = layerFull.name || layerBasic.name || `Layer ${id}`;
    const geom = this._normalizeGeom(layerFull.geometryType);
    const key  = `${svcKey}__M${id}`;

    const rendererType = layerFull?.drawingInfo?.renderer?.type || '';
    return {
      _key      : key,
      _svcKey   : svcKey,
      _svcUrl   : svcUrl,
      _svcType  : 'MapServer',
      _svcName  : svcName,
      _layerId  : id,
      _geom     : geom,
      _layerName: name,
      // Config is assembled at generate-time (grouped by svcKey)
      id        : this._slug(`${svcName}_${name}_${id}`),
      title     : name,
      type      : 'map-image',
      url       : svcUrl,         // MapImageLayer uses service root URL
      geometryType: geom,
      visible   : true,
      opacity   : 1,
      symbol    : this._extractSymbol(layerFull.drawingInfo?.renderer, geom),
      rendererType: rendererType,
      isSymbolEditable: !rendererType || rendererType === 'simple',
      labelsVisible: !!(layerFull.drawingInfo?.labelingInfo?.length),
      labelingInfo : layerFull.drawingInfo?.labelingInfo || undefined,
      popupTemplate: this._buildPopup(name, layerFull.fields || [], layerFull.displayField),
      fields      : (layerFull.fields || []).map(f => ({ name: f.name, alias: f.alias, type: f.type })),
    };
  }

  _buildImageryConfig (svcKey, svcUrl, svcName, info) {
    const key = `${svcKey}__IMG`;
    return {
      _key    : key,
      _svcKey : svcKey,
      _svcUrl : svcUrl,
      _svcType: 'ImageServer',
      _svcName: svcName,
      _layerId: null,
      _geom   : 'imagery',
      id      : this._slug(svcName),
      title   : info.name || svcName,
      type    : 'imagery',
      url     : svcUrl,
      visible : true,
      opacity : 1,
      fields  : (info.fields || []).map(f => ({ name: f.name, alias: f.alias, type: f.type })),
      popupTemplate: this._buildPopup(svcName, info.fields || [], info.displayField),
    };
  }

  _buildWMSConfig (svcKey, svcUrl, svcName) {
    const key = `${svcKey}__WMS`;
    return {
      _key    : key,
      _svcKey : svcKey,
      _svcUrl : svcUrl,
      _svcType: 'WMSServer',
      _svcName: svcName,
      _layerId: null,
      _geom   : 'wms',
      id      : this._slug(svcName),
      title   : svcName,
      type    : 'wms',
      url     : svcUrl,
      visible : true,
      opacity : 1,
      sublayers: [{ name: '0' }],
    };
  }

  // ── Symbol extraction ─────────────────────────────────────────────────────────

  _extractSymbol (renderer, geomType) {
    const def = this._defaultSymbol(geomType);
    if (!renderer || renderer.type !== 'simple') return def;
    const s = renderer.symbol;
    if (!s) return def;

    const result = { ...def };
    if (s.color && Array.isArray(s.color)) result.color = this._rgbToHex(s.color);
    if (s.outline?.color && Array.isArray(s.outline.color)) result.outlineColor = this._rgbToHex(s.outline.color);
    if (s.size  != null) result.size  = s.size;
    if (s.width != null) result.width = s.width;
    if (s.style)         result.style = s.style;
    return result;
  }

  _defaultSymbol (geomType) {
    switch (geomType) {
      case 'point'  : return { color:'#0078d4', outlineColor:'#ffffff', size:8,   style:'circle', width:1 };
      case 'polyline': return { color:'#0078d4', outlineColor:'#ffffff', width:2,  style:'solid',  size:8  };
      case 'polygon' : return { color:'#0078d4', outlineColor:'#ffffff', width:1.5,style:'solid',  size:8  };
      default        : return { color:'#0078d4', outlineColor:'#ffffff', size:8,   style:'circle', width:1 };
    }
  }

  // ── Popup builder ─────────────────────────────────────────────────────────────

  _buildPopup (title, fields, displayField) {
    // Filter out geometry/OID/BLOB fields from popup
    const SKIP_TYPES = new Set([
      'esriFieldTypeGeometry','esriFieldTypeBlob','esriFieldTypeRaster',
      'esriFieldTypeXML','esriFieldTypeGlobalID',
    ]);
    const visible = fields.filter(f =>
      !SKIP_TYPES.has(f.type) &&
      !['shape','shape_length','shape_area','globalid'].includes(f.name?.toLowerCase())
    );

    return {
      title  : displayField ? `{${displayField}}` : title,
      content: [{
        type      : 'fields',
        fieldInfos: visible.map(f => ({
          fieldName: f.name,
          label    : f.alias || f.name,
          visible  : true,
          format   : f.type === 'esriFieldTypeDate'
            ? { dateFormat: 'short-date-short-time' }
            : (f.type?.includes('Double') || f.type?.includes('Float'))
              ? { places: 2, digitSeparator: true } : undefined,
        })),
      }],
    };
  }

  // ── Tree rendering ────────────────────────────────────────────────────────────

  _renderTreeLoading () {
    const wrap = this._panel.querySelector('#lcb-tree');
    if (wrap) wrap.innerHTML = '<div class="lcb-loading"><span class="lcb-spinner"></span> Loading services…</div>';
  }

  _renderTree () {
    const wrap = this._panel.querySelector('#lcb-tree');
    if (!wrap) return;

    if (this._loadError) {
      wrap.innerHTML = `<div class="lcb-error">
        ✗ Failed to load directory.<br>
        <small>${this._loadError.message}</small><br>
        <small>Check the URL, CORS, or authentication settings.</small>
      </div>`;
      return;
    }

    if (!this._treeData) { wrap.innerHTML = ''; return; }

    const { folders, services } = this._treeData;
    const totalLayers = this._layerDataMap.size;

    let html = `<div class="lcb-tree">
      <div class="lcb-tree-summary">${totalLayers} mappable layer${totalLayers !== 1 ? 's' : ''} found</div>`;

    // Root-level services
    if (services.length) {
      html += `<div class="lcb-folder"><div class="lcb-folder-label">/ (root)</div>`;
      html += services.map(s => this._renderService(s)).join('');
      html += `</div>`;
    }

    // Folders
    for (const folder of folders) {
      if (!folder.services.length) continue;
      html += `
        <div class="lcb-folder">
          <button class="lcb-folder-row" data-action="expandFolder" data-folder="${folder.name}">
            <span class="lcb-folder-arrow">▶</span>
            <span class="lcb-folder-icon">📁</span>
            <span class="lcb-folder-name">${folder.name}</span>
            <span class="lcb-folder-count">${folder.services.length} svc</span>
          </button>
          <div class="lcb-folder-body" hidden>
            ${folder.services.map(s => this._renderService(s)).join('')}
          </div>
        </div>`;
    }

    html += `</div>`;
    wrap.innerHTML = html;

    // Folder expand/collapse (delegated since HTML was just set)
    wrap.addEventListener('click', e => {
      const btn = e.target.closest('[data-action="expandFolder"]');
      if (!btn) return;
      const body  = btn.nextElementSibling;
      const arrow = btn.querySelector('.lcb-folder-arrow');
      if (body) body.hidden = !body.hidden;
      if (arrow) arrow.textContent = body?.hidden ? '▶' : '▼';
    });
  }

  _renderService (svc) {
    const layersHtml = svc.layers.map(l => this._renderLayer(l)).join('');
    const typeClass  = `lcb-badge--${svc.type.toLowerCase().replace('server','')}`;
    const svcKey     = svc.key;
    const layerCount = svc.layers.length;

    return `
      <div class="lcb-service" data-svc-key="${svcKey}">
        <div class="lcb-svc-row">
          <button class="lcb-svc-expand" data-action="expandSvc" data-svc-key="${svcKey}"
            title="Expand/collapse layers">
            <span class="lcb-svc-arrow">▶</span>
          </button>
          <span class="lcb-badge ${typeClass}">${TYPE_BADGE[svc.type] || '?'}</span>
          <span class="lcb-svc-name" title="${svc.url}">${svc.name}</span>
          <span class="lcb-svc-count">${layerCount}</span>
          <label class="lcb-check-all" title="Select all layers in this service">
            <input type="checkbox" data-svc-all-key="${svcKey}">
          </label>
        </div>
        <div class="lcb-svc-body" data-svc-layers="${svcKey}" hidden>
          ${layersHtml}
        </div>
      </div>`;
  }

  _renderLayer (lyr) {
    const geomIcon = GEOM_ICON[lyr._geom] || '?';
    const geomLabel = lyr._geom || 'unknown';
    return `
      <div class="lcb-layer-row" data-layer-key="${lyr._key}">
        <label class="lcb-layer-check">
          <input type="checkbox" data-layer-key="${lyr._key}"
            ${this._selectedKeys.has(lyr._key) ? 'checked' : ''}>
          <span class="lcb-check-box"></span>
        </label>
        <span class="lcb-geom-icon lcb-geom--${lyr._geom}" title="${geomLabel}">${geomIcon}</span>
        <span class="lcb-layer-name" title="${lyr.url}">${lyr.title}</span>
        <span class="lcb-layer-id">id:${lyr._layerId ?? '—'}</span>
      </div>`;
  }

  _toggleServiceExpand (svcKey) {
    const body  = this._panel.querySelector(`[data-svc-layers="${svcKey}"]`);
    const arrow = this._panel.querySelector(`[data-svc-key="${svcKey}"] .lcb-svc-arrow`);
    if (body) body.hidden = !body.hidden;
    if (arrow) arrow.textContent = body?.hidden ? '▶' : '▼';
  }

  // ── Selection UI ──────────────────────────────────────────────────────────────

  _updateSelectionUI () {
    const count = this._selectedKeys.size;
    const el    = this._panel.querySelector('#lcb-sel-count');
    if (el) el.textContent = `${count} layer${count !== 1 ? 's' : ''} selected`;
    this._setFooterVisible(this._layerDataMap.size > 0);
  }

  _onClearAll () {
    this._selectedKeys.clear();
    this._panel.querySelectorAll('[data-layer-key]').forEach(cb => cb.checked = false);
    this._panel.querySelectorAll('[data-svc-all-key]').forEach(cb => cb.checked = false);
    this._updateSelectionUI();
  }

  // ── Config generation ─────────────────────────────────────────────────────────

  /**
   * Build the final layerConfigs array.
   * MapServer layers selected from the same service are merged into one
   * map-image config entry with a sublayers array.
   */
  _buildConfig () {
    const configs   = [];
    const mapGroups = new Map(); // svcKey → { config, sublayers[] }
    const idSet     = new Set();

    for (const key of this._selectedKeys) {
      const lyr = this._layerDataMap.get(key);
      if (!lyr) continue;

      if (lyr._svcType === 'MapServer') {
        // Prefer FeatureLayer for selected MapServer sublayers to avoid blank exports
        // seen on some servers when rendered as MapImageLayer.
        if (lyr.geometryType && lyr._layerId != null) {
          const asFeature = {
            ...this._cleanConfig(lyr, idSet),
            type: 'feature',
            url: `${lyr._svcUrl}/${lyr._layerId}`,
            geometryType: lyr.geometryType,
          };
          configs.push(asFeature);
          continue;
        }

        // Non-feature map sublayers fallback to map-image grouping.
        if (!mapGroups.has(lyr._svcKey)) {
          const baseCfg = {
            id      : this._ensureUniqueId(this._slug(lyr._svcName), idSet),
            title   : lyr._svcName,
            type    : 'map-image',
            url     : lyr._svcUrl,
            visible : true,
            opacity : 1,
            sublayers: [],
          };
          mapGroups.set(lyr._svcKey, baseCfg);
          configs.push(baseCfg);
        }
        mapGroups.get(lyr._svcKey).sublayers.push({
          id     : lyr._layerId,
          title  : lyr._layerName || lyr.title,
          visible: true,
          popupEnabled: !!lyr.popupTemplate,
          popupTemplate: lyr.popupTemplate || undefined,
        });
      } else {
        // FeatureLayer, ImageServer, WMS — each is a standalone entry
        const clean = this._cleanConfig(lyr, idSet);
        configs.push(clean);
      }
    }

    return configs;
  }

  /** Remove internal _* fields from the config before export. */
  _cleanConfig (lyr, idSet) {
    const {
      _key, _svcKey, _svcUrl, _svcType, _svcName, _layerId, _geom, _layerName,
      ...rest
    } = lyr;
    rest.id = this._ensureUniqueId(rest.id, idSet);
    // Keep fields only as internal hint — remove from clean output
    // (user can strip them later; they're useful for ArcGIS FeatureLayer)
    return rest;
  }

  _onSave () {
    if (!this._selectedKeys.size) {
      alert('Select at least one layer first.'); return;
    }
    this._generatedCfg = this._buildConfig();
    if (!this._generatedCfg?.length) return;

    const btn = this._panel.querySelector('[data-action="saveSelection"]');
    if (btn) { btn.textContent = 'Added'; setTimeout(() => (btn.textContent = 'Add Selected Layers'), 1200); }

    if (typeof this._onSaveCallback === 'function') this._onSaveCallback(this._generatedCfg);
  }

  async _onSync () {
    if (!this._generatedCfg || !this._syncEndpoint) return;
    const statusEl = this._panel.querySelector('#lcb-sync-status');
    this._setStatus('lcb-sync-status', '⏳ Syncing…');
    try {
      const resp = await fetch(this._syncEndpoint, {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ configs: this._generatedCfg }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      this._setStatus('lcb-sync-status', '✓ Synced', 'ok');
    } catch (err) {
      this._setStatus('lcb-sync-status', `✗ ${err.message}`, 'err');
    }
  }

  // ── Utilities ─────────────────────────────────────────────────────────────────

  /** Fetch with esriRequest; appends manual token if set. */
  async _fetch (url) {
    const { esriRequest } = this._esri;
    const opts = {
      query       : { f: 'json' },
      responseType: 'json',
      timeout     : REQUEST_TIMEOUT,
    };
    if (this._token) opts.query.token = this._token;
    const resp = await esriRequest(url, opts);
    if (resp.data?.error) throw new Error(resp.data.error.message || 'Service error');
    return resp.data;
  }

  /** Fetch multiple URLs in parallel, returning results array (null on failure). */
  async _fetchParallel (urls) {
    return Promise.all(urls.map(u => this._fetch(u).catch(() => null)));
  }

  /**
   * Run tasks with a concurrency cap.
   * @param {Array} items
   * @param {Function} fn  async fn(item) => void
   */
  async _runThrottled (items, fn) {
    const queue = [...items];
    const workers = Array.from({ length: Math.min(MAX_CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const item = queue.shift();
        try { await fn(item); } catch { /* skip failed services */ }
      }
    });
    await Promise.all(workers);
  }

  _normalizeGeom (esriType) {
    if (!esriType) return null;
    const t = esriType.toLowerCase();
    if (t.includes('point'))   return 'point';
    if (t.includes('line'))    return 'polyline';
    if (t.includes('polygon')) return 'polygon';
    return null;
  }

  _rgbToHex (arr) {
    return '#' + arr.slice(0, 3).map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
  }

  _slug (str) {
    return (str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, 64) || 'layer';
  }

  _ensureUniqueId (id, idSet) {
    if (!idSet.has(id)) { idSet.add(id); return id; }
    let i = 2;
    while (idSet.has(`${id}_${i}`)) i++;
    const unique = `${id}_${i}`;
    idSet.add(unique);
    return unique;
  }

  _setStatus (elId, msg, type = '') {
    const el = this._panel.querySelector(`#${elId}`);
    if (!el) return;
    el.textContent = msg;
    el.className   = `lcb-status ${type ? 'lcb-status--' + type : ''}`;
  }

  _setFooterVisible (visible) {
    const footer = this._panel.querySelector('#lcb-footer');
    if (footer) footer.hidden = !visible;
  }

  _attachPanel () {
    let container = this._view?.container;
    if (typeof container === 'string') container = document.getElementById(container);
    const target = container || document.body;
    if (container) target.style.position = 'relative';
    target.appendChild(this._panel);
  }

  // ── Public API ────────────────────────────────────────────────────────────────

  /**
   * Programmatically set a pre-existing token.
   * @param {string} token
   * @param {string} [serverUrl] — registers with IdentityManager if provided
   */
  setToken (token, serverUrl) {
    const { IdentityManager } = this._esri;
    this._token = token;
    const input = this._panel.querySelector('#lcb-manual-token');
    if (input) input.value = token;
    if (serverUrl) IdentityManager.registerToken({ server: serverUrl, token });
  }

  /**
   * Programmatically trigger username/password token generation.
   * @param {string} username
   * @param {string} password
   * @param {string} serverUrl — root services URL
   */
  async generateTokenFor (username, password, serverUrl) {
    const input = this._panel.querySelector('#lcb-username');
    const passEl = this._panel.querySelector('#lcb-password');
    const urlEl  = this._panel.querySelector('#lcb-root-url');
    if (input)  input.value  = username;
    if (passEl) passEl.value = password;
    if (urlEl && serverUrl) urlEl.value = serverUrl;
    await this._onGenerateToken();
  }

  /**
   * Load a URL programmatically (equivalent to entering URL and clicking Load).
   * @param {string} url
   */
  async loadUrl (url) {
    const input = this._panel.querySelector('#lcb-root-url');
    if (input) input.value = url;
    await this._onLoad();
  }

  /**
   * Get the last generated configs array (or null).
   * @returns {LayerConfig[]|null}
   */
  getGeneratedConfigs () { return this._generatedCfg; }

  /**
   * Load configs previously saved to localStorage.
   * @returns {LayerConfig[]}
   */
  static loadFromStorage () {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  }

  /**
   * Clear the saved configs from localStorage.
   */
  static clearStorage () { localStorage.removeItem(STORAGE_KEY); }

  /** Destroy widget and remove from DOM. */
  destroy () {
    this._panel?.remove();
    this._panel = null;
  }
}

